import React, { useMemo } from 'react';
import { CalendarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Button } from '@theraply/ui';
import { AvailabilitySlot } from '../../hooks/useTherapistProfile';

interface AvailabilityPreviewProps {
  slots: AvailabilitySlot[];
  onSlotClick: (slot: AvailabilitySlot) => void;
  onSeeAll: () => void;
}

/**
 * Availability preview component showing next 2 weeks.
 *
 * Features:
 * - Simple 2-week calendar grid
 * - Highlighted available slots
 * - Click slot to start booking flow
 * - "See all availability" link
 *
 * Accessibility:
 * - Keyboard navigation
 * - ARIA labels
 * - Focus management
 *
 * Performance:
 * - Memoized date grouping
 * - Efficient rendering
 */
export const AvailabilityPreview: React.FC<AvailabilityPreviewProps> = ({
  slots,
  onSlotClick,
  onSeeAll,
}) => {
  // Group slots by date
  const slotsByDate = useMemo(() => {
    const grouped = new Map<string, AvailabilitySlot[]>();

    slots.forEach((slot) => {
      const existing = grouped.get(slot.date) || [];
      existing.push(slot);
      grouped.set(slot.date, existing);
    });

    return grouped;
  }, [slots]);

  // Get sorted dates
  const sortedDates = useMemo(() => {
    return Array.from(slotsByDate.keys()).sort();
  }, [slotsByDate]);

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      dayOfWeek: date.toLocaleDateString('en-US', { weekday: 'short' }),
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      day: date.getDate(),
      full: date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      }),
    };
  };

  // Format time
  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (slots.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">No availability in the next 2 weeks</p>
        <p className="text-sm text-gray-500 mt-1">Please check back later or contact the therapist</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Available Dates Grid */}
      <div className="space-y-4">
        {sortedDates.slice(0, 7).map((dateString) => {
          const dateSlots = slotsByDate.get(dateString) || [];
          const dateInfo = formatDate(dateString);

          return (
            <div key={dateString} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Date Header */}
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5 text-indigo-600" />
                  <h3 className="font-semibold text-gray-900">{dateInfo.full}</h3>
                </div>
              </div>

              {/* Time Slots */}
              <div className="p-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {dateSlots.slice(0, 8).map((slot, index) => (
                    <button
                      key={`${slot.date}-${slot.startTime}-${index}`}
                      onClick={() => onSlotClick(slot)}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-indigo-300 rounded-lg text-indigo-600 hover:bg-indigo-50 hover:border-indigo-400 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      aria-label={`Book appointment at ${formatTime(slot.startTime)} on ${dateInfo.full}`}
                    >
                      <ClockIcon className="h-4 w-4" />
                      <span className="font-medium">{formatTime(slot.startTime)}</span>
                    </button>
                  ))}
                </div>

                {/* Show more slots indicator */}
                {dateSlots.length > 8 && (
                  <p className="text-sm text-gray-500 mt-3 text-center">
                    +{dateSlots.length - 8} more slots available
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Show more dates indicator */}
      {sortedDates.length > 7 && (
        <p className="text-sm text-gray-500 text-center">
          Showing first 7 days with availability
        </p>
      )}

      {/* See All Button */}
      <div className="text-center pt-4">
        <Button variant="outline" onClick={onSeeAll} className="min-w-[200px]">
          See All Availability
        </Button>
      </div>
    </div>
  );
};
